import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import {
  createDirectSalesProductTemplate,
  toggleDirectSalesProductStatus,
} from 'modules/api/directSalesProductTemplate';
import { cancelFunctions } from 'utils/cancelFunctions';
import { setIsSavingProduct, syncActiveLineFromNewProduct } from 'store/productManagement/reducer';
import { addToastNotification } from 'store/notification/reducer';
import { SelectionPermission, ProductLine, ProductManagementFields } from 'store/productManagement/reducer.types';
import { Store } from 'components/common/types/Store.types';
import {
  DirectSalesProductTemplateStatus,
  DirectSalesProductTemplateLine,
} from 'components/common/types/DirectSalesProductTemplate.types';
import { NAVIGATION_PATHS } from 'consts/navigation';
import { hasProductRequiredFields } from 'utils/hasProductRequiredFields';
import { getNewProduct } from 'store/productManagement/selectors';
import { getMarketId } from 'store/publisher/selectors';
import { transformDirectSalesProductToRules } from '../Products/utils/transformDirectSalesProductToRules';

const SYNCED_LINE_FIELDS: (keyof ProductManagementFields)[] = [
  'mediaType',
  'pricingMode',
  'range',
  'channels',
  'locations',
  'formats',
  'budget',
  'frames',
  'impressions',
  'frameList',
  'poi',
  'tags',
  'sot',
  'sweep',
  'networks',
  'objectives',
];

const pickSyncedFields = (source: ProductManagementFields): Partial<ProductManagementFields> => {
  return SYNCED_LINE_FIELDS.reduce<Partial<ProductManagementFields>>((result, field) => {
    if (source[field] === undefined) {
      return result;
    }
    return { ...result, [field]: source[field] };
  }, {});
};

/* eslint-disable sonarjs/cognitive-complexity */
export const useCreateProductTemplate = (): (() => Promise<void>) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const marketId = useSelector(getMarketId);
  const newProduct = useSelector(getNewProduct);
  const activeLineId = useSelector((state: Store) => state.productManagement.activeLineId);

  const { name, folder, mediaType, channels, pricingMode, range, budget, frames, impressions, networks } = newProduct;

  const productLines = useSelector((state: Store) => state.productManagement.productLines);
  const originalProductId = useSelector((state: Store) => state.productManagement.originalProductId);
  const intendedOriginalProductState = useSelector(
    (state: Store) => state.productManagement.intendedOriginalProductState,
  );

  const getSyncedProductLines = (): ProductLine[] => {
    const syncedFields = pickSyncedFields(newProduct);
    return productLines.map((line) => {
      if (line.lineId === activeLineId) {
        return {
          ...line,
          ...syncedFields,
          objectives: syncedFields.objectives ?? { selectionPermission: SelectionPermission.DISABLED, value: [] },
        };
      }
      return line;
    });
  };

  const transformLineToProductLine = (line: ProductLine): DirectSalesProductTemplateLine => {
    const lineRules = transformDirectSalesProductToRules(line);
    return {
      lineName: line.name,
      rules: lineRules,
    };
  };

  return async (): Promise<void> => {
    if (!name) {
      dispatch(
        addToastNotification({
          header: 'Missing product name',
          description: 'Please input product name to save product',
          type: 'Failure',
        }),
      );

      return;
    }

    if (!channels) {
      dispatch(
        addToastNotification({
          header: 'Missing channels',
          description: 'Please input channels to save product',
          type: 'Failure',
        }),
      );

      return;
    }

    if (!mediaType) {
      dispatch(
        addToastNotification({
          header: 'Missing media type',
          description: 'Please input media type to save product',
          type: 'Failure',
        }),
      );

      return;
    }

    const areNetworksEmpty = isEmpty(networks) || networks.selectionPermission === SelectionPermission.DISABLED;

    const hasRequiredFields = hasProductRequiredFields({
      mediaType,
      channels,
      pricingMode,
      range,
      budget,
      impressions,
      frames,
      areNetworksEmpty,
    });

    if (!hasRequiredFields) {
      dispatch(
        addToastNotification({
          id: '2',
          header: 'Missing fields',
          description: 'Please input all required fields',
          type: 'Failure',
        }),
      );

      return;
    }

    if (!folder) {
      dispatch(
        addToastNotification({
          id: '3',
          header: 'Missing product folder',
          description: 'Please choose or create new folder to save product to',
          type: 'Failure',
        }),
      );

      return;
    }

    try {
      dispatch(setIsSavingProduct(true));
      dispatch(syncActiveLineFromNewProduct());

      const syncedLines = getSyncedProductLines();
      const lines = syncedLines.map(transformLineToProductLine);

      const productData = {
        name,
        folder,
        status: DirectSalesProductTemplateStatus.ACTIVE,
        lines,
      };

      const savedProduct = await createDirectSalesProductTemplate(productData, marketId, cancelFunctions);

      if (savedProduct?.id) {
        if (intendedOriginalProductState && originalProductId) {
          try {
            await toggleDirectSalesProductStatus(
              { status: intendedOriginalProductState as 'Disabled' | 'Active' },
              marketId,
              originalProductId,
              cancelFunctions,
            );
            // eslint-disable-next-line no-empty
          } catch {}
        }

        dispatch(
          addToastNotification({
            type: 'Success',
            header: 'Product saved',
            description: 'Product template has been saved successfully',
            timeout: 10000,
          }),
        );

        dispatch(setIsSavingProduct(false));
        navigate(NAVIGATION_PATHS.PRODUCT_MANAGEMENT);
      } else {
        dispatch(setIsSavingProduct(false));
      }
    } catch {
      dispatch(setIsSavingProduct(false));
    }
  };
};
/* eslint-enable sonarjs/cognitive-complexity */
